
import { link } from 'bits/autolinker'
import { getDisplayAttributes } from 'bits/beethoven'
import { isAppUsing } from 'bits/flip'
import { $ } from 'bits/jquery'
import { getHostnameFromUrl, transformUrl } from 'bits/location'
import { mathifyElement } from 'bits/math'
import { PADLET_DOMAIN_REGEX, SLIDESHOW_URL_REGEX } from 'bits/regex'
import { isInternalUrl, NATIVE_HOST } from 'bits/url'
import { Component, Getter, Prop, Vue, Watch } from 'bits/vue'
import emitClickOnTouch from 'vuedirectives/emit_click_on_touch'

const autoLink = (text: string): Promise<string> => {
  return link(text, {
    replaceFn: (match) => {
      const tag = match.buildTag()
      tag.setAttr('rel', 'noopener nofollow ugc')
      tag.setAttr('data-bridged-link', 'external')
      const href = tag.getAttr('href')
      if (isAppUsing('redirectionWarning') && !isInternalUrl(href)) {
        tag.setAttr(
          'href',
          transformUrl(`https://${NATIVE_HOST}`, {
            path: 'redirect',
            search: {
              url: href,
            },
          }),
        )
      }
      return tag
    },
  })
}

@Component({
  directives: {
    emitClickOnTouch,
  },
})
export default class SurfacePostBody extends Vue {
  $refs!: {
    postBody: HTMLParagraphElement
  }

  @Prop({ required: true }) content!: string
  @Prop({ default: 'auto' }) darkMode!: boolean | 'auto'
  @Prop({ default: false }) isTextSelectable!: boolean

  @Getter isApp!: boolean

  get isUsingPostClickExperiment(): boolean {
    return isAppUsing('postClickExperiment') && !this.isApp
  }

  body = ''

  mounted(): void {
    this.performPostUpdateActions()
  }

  @Watch('content')
  onContentChange(): void {
    this.performPostUpdateActions()
  }

  async performPostUpdateActions(): Promise<void> {
    this.body = await autoLink(this.content)
    this.$nextTick(() => {
      this.mathifyPostBody()
      this.setExternalLinksForNativeBridge()
      if (this.isUsingPostClickExperiment) {
        this.stopEventPropagationFromLinks()
      }
      this.$nextTick(() => {
        this.makeBodyPadletLinksInternalForNativeBridge()
      })
    })
  }

  mathifyPostBody(): void {
    $(this.$refs.postBody)
      .find('var')
      .each((_, el) => {
        if (!el.children[0] || el.children[0].className !== 'katex') {
          mathifyElement(el)
        }
      })
  }

  setExternalLinksForNativeBridge(): void {
    $(this.$refs.postBody)
      .find('a[href^=h]')
      .attr({ target: '_blank', rel: 'noopener nofollow ugc', 'data-bridged-link': 'external' })
  }

  stopEventPropagationFromLinks(): void {
    if (!this.$refs.postBody) return
    this.$refs.postBody.querySelectorAll('a').forEach((a: HTMLAnchorElement) => {
      a.addEventListener('click', (e: MouseEvent) => {
        e.stopPropagation()
      })
    })
  }

  // check to see if the url links to a padlet and set data-bridged-link to internal
  async makeBodyPadletLinksInternalForNativeBridge(): Promise<void> {
    if (!this.isApp || !this.$refs.postBody) return

    const bodyAnchorTags = this.$refs.postBody.querySelectorAll('a[href^=h]')
    for (const anchorTag of bodyAnchorTags) {
      const hrefUrl = anchorTag.getAttribute('href')
      if (!hrefUrl) continue

      const hrefHost = getHostnameFromUrl(hrefUrl)
      if (PADLET_DOMAIN_REGEX.test(hrefHost)) {
        // the fetchLink request in bits/beethoven.ts is memoized so this shouldn't fire too many requests
        const urlDisplayAttributes = await getDisplayAttributes(hrefUrl)

        const urlGoesToSlideshow = SLIDESHOW_URL_REGEX.test(hrefUrl)

        if (urlGoesToSlideshow) {
          anchorTag.setAttribute('data-bridged-link', 'slideshow')
        } else {
          // All pages from padlet have content_subcategory === 'padlet'
          // provider_name for a wall is the builder's name, "padlet" should be used only for landside pages (e.g. padlet.com, padlet.com/features)
          const isPadletWallUrl =
            urlDisplayAttributes?.content_subcategory === 'padlet' &&
            urlDisplayAttributes?.provider_name.toLowerCase() !== 'padlet'

          if (isPadletWallUrl) {
            anchorTag.setAttribute('data-bridged-link', 'padlet')
          }
        }
      }
    }
  }
}
